// Parity P6a: the agent policy (hybrid design D3, §8.1). Wave 0 serves the preview the
// Permissions tab and Board settings › Agents read: for one seat, optionally over some boards,
// every action class's effective verdict and the level it came from. D10 (wave 1, the scope
// editor) adds the seat's policy as the Permissions tab reads and edits it (`GET|PUT
// seats/{id}/agent-policy`, `If-Match` on the version), the preview of an edit (`modes=`), and
// the explainer ("can this agent do X on Y?", `GET …/agent-policy/explain`). The company ceiling
// routes and Board settings › Agents stay P6a/P4 work.
import { z } from "zod";
import { AgentRefusal } from "../approvals";
import { AgentActionClass, AgentPolicyMode } from "../enums";
import { boundedRecord, Uuid } from "../primitives";
import { CompanyItemParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";

/** What the server answers an agent action with (design §1.1). */
export const AgentVerdict = z
  .enum(["allow_in_run", "propose", "deny"])
  .meta({ id: "AgentVerdict" });
export type AgentVerdict = z.infer<typeof AgentVerdict>;

/**
 * Where an effective verdict came from: the class default, a policy row (company ceiling, seat,
 * a legacy `seat.settings.auto_approve/require_approval` key, a board), or a rule no row can
 * change (`never`: a hard prohibition; `fence`: a board fence; `unknown_class`: fail closed;
 * `bulk`: the affected set is over 10).
 */
export const AgentPolicySource = z
  .object({
    level: z.enum([
      "default",
      "company",
      "seat",
      "legacy",
      "board",
      "never",
      "fence",
      "unknown_class",
      "bulk",
      // TEMPLATES-T3/T4: an overlay's blocked class (Never) or the DE's probation (Ask).
      "template",
      // AGENT-GUARDRAILS: an always-Ask class (bulk, over 10 tasks) — no row makes it Direct.
      "guardrail",
    ]),
    /** The company, seat or board whose row decided; null for the rest. */
    scope_id: Uuid.nullable(),
    /** The mode of that level (`direct` · `ask` · `never`). */
    mode: AgentPolicyMode.exclude(["inherit"]),
  })
  .meta({ id: "AgentPolicySource" });
export type AgentPolicySource = z.infer<typeof AgentPolicySource>;

export const AgentPolicyPreviewQuery = z
  .object({
    /** Comma-separated board ids the operation touches (every one is evaluated). */
    boardIds: z
      .string()
      .max(37 * 50)
      .optional()
      .describe("Comma-separated board ids (at most 50)"),
    /**
     * D10: an edit to preview, `class:mode` pairs (`inherit` drops the seat's row), laid over the
     * stored seat rows before the same rule decides. Unknown classes are 422.
     */
    modes: z
      .string()
      .max(64 * 40)
      .optional()
      .describe("Comma-separated class:mode pairs to preview (the seat level)"),
  })
  .meta({ id: "AgentPolicyPreviewQuery" });

export const AgentPolicyPreviewRow = z
  .object({
    class: AgentActionClass,
    verdict: AgentVerdict,
    source: AgentPolicySource,
    /** The class's built-in default (design §8.1 table). */
    default_mode: AgentPolicyMode.exclude(["inherit"]),
    /**
     * What the agent meets on today's write paths where that differs from the policy's verdict
     * (a default Ask no route gate holds; 05's gate that always proposes); else the verdict.
     */
    in_practice: AgentVerdict,
  })
  .meta({ id: "AgentPolicyPreviewRow" });

/** A board fence (P4's Board settings › Agents): agents cannot read, or not write until a date. */
export const AgentBoardFence = z
  .object({
    board_id: Uuid,
    kind: z.enum(["read", "write"]),
    until: z.string().nullable(),
  })
  .meta({ id: "AgentBoardFence" });

export const AgentPolicyPreview = z
  .object({
    seat_id: Uuid,
    board_ids: z.array(Uuid),
    classes: z.array(AgentPolicyPreviewRow),
    /** The hard prohibitions (§8.1 `never` row): not configurable, never approvable. */
    never: z.array(z.string()),
    /** The fences of the boards named (none until P4 registers its provider). */
    fences: z.array(AgentBoardFence),
  })
  .meta({ id: "AgentPolicyPreview" });
export type AgentPolicyPreview = z.infer<typeof AgentPolicyPreview>;

// D10: the seat's policy (the Permissions tab) -----------------------------------------------

const EffectiveMode = AgentPolicyMode.exclude(["inherit"]);

export const SeatAgentPolicyClass = z
  .object({
    class: AgentActionClass,
    /** What the class covers (design §8.1 table). */
    operations: z.string(),
    /** The route's hard limit beside it, which no row lifts; null when none. */
    limit: z.string().nullable(),
    default_mode: EffectiveMode,
    /** The seat's own row (`inherit`: none, so the legacy key or the default applies). */
    seat_mode: AgentPolicyMode,
    /** The seat's legacy `auto_approve` / `require_approval` key for it, if any. */
    legacy_mode: EffectiveMode.nullable(),
    /** The company ceiling's row, if any (the most permissive mode a seat may have). */
    ceiling_mode: EffectiveMode.nullable(),
    /**
     * Every write path of the class holds its setting today, so it can be set; the others are
     * shown read-only (a PUT setting one is 422 `not_enforced`; `inherit` always works).
     * CORE-KEYS: where the company's `settings.flags.core_keys` is on, the dispatch gate holds
     * every class on MCP, REST and admission, so all 22 are `enforced` there.
     */
    enforced: z.boolean(),
    /** The modes a seat may be given for it (empty when not `enforced`). */
    settable_modes: z.array(EffectiveMode),
    /** The effective verdict over no board, and its source (the preview's). */
    verdict: AgentVerdict,
    source: AgentPolicySource,
    /** What the agent meets on today's write paths (see the preview row). */
    in_practice: AgentVerdict,
    /** Why `in_practice` differs from the verdict, or where it is not held; null otherwise. */
    note: z.string().nullable(),
  })
  .meta({ id: "SeatAgentPolicyClass" });

export const SeatAgentKey = z
  .object({
    id: Uuid,
    key_prefix: z.string(),
    scopes: z.array(z.string()),
    expires_at: z.string().nullable(),
    created_at: z.string(),
    /** What a run started from this key may use of the work families (read-only). */
    work: z.object({
      tasks: z.object({ read: z.boolean(), write: z.boolean() }),
      boards: z.object({ read: z.boolean(), write: z.boolean() }),
      files: z.object({ read: z.boolean(), write: z.boolean() }),
    }),
  })
  .meta({ id: "SeatAgentKey" });

export const SeatAgentPolicy = z
  .object({
    seat_id: Uuid,
    /** The `If-Match` a `PUT` names (also the `ETag` header). */
    version: z.string(),
    classes: z.array(SeatAgentPolicyClass),
    /** Seat rows of a class this code does not know (decided Ask, never widened); read-only. */
    unknown_rows: z.array(z.object({ class: z.string(), mode: AgentPolicyMode })),
    /** The hard prohibitions: not configurable, never approvable. */
    never: z.array(z.string()),
    /** The seat's live agent keys and their scopes (read-only; managed on the Keys tab). */
    keys: z.array(SeatAgentKey),
    /** Whether the caller may change the policy (managers). */
    editable: z.boolean(),
  })
  .meta({ id: "SeatAgentPolicy" });
export type SeatAgentPolicy = z.infer<typeof SeatAgentPolicy>;

export const SeatAgentPolicyPut = z
  .strictObject({
    /** A mode per class to set (`inherit` removes the seat's row); classes not named stay. */
    modes: boundedRecord(AgentPolicyMode, 40, z.string().max(64)).refine(
      (m) => Object.keys(m).length >= 1,
      { message: "at least one class" },
    ),
  })
  .meta({ id: "SeatAgentPolicyPut" });
export type SeatAgentPolicyPut = z.infer<typeof SeatAgentPolicyPut>;

export const AgentPolicyExplainQuery = z
  .object({
    /** The action class asked about (or `tool`). */
    class: z.string().max(64).optional(),
    /** An MCP tool name, read as the class it carries. */
    tool: z.string().max(64).optional(),
    boardIds: AgentPolicyPreviewQuery.shape.boardIds,
    /** A task: its board is the board asked about. */
    taskId: Uuid.optional(),
    /** How many items the operation touches (over 10 is `bulk`). */
    affectedCount: z.coerce.number().int().min(0).max(100_000).optional(),
  })
  .meta({ id: "AgentPolicyExplainQuery" });

export const AgentPolicyStep = z
  .object({
    level: AgentPolicySource.shape.level,
    scope_id: Uuid.nullable(),
    /** What that level says (null: no row there, or the check does not apply). */
    mode: EffectiveMode.nullable(),
    /** It decided, or tightened, the verdict. */
    applied: z.boolean(),
  })
  .meta({ id: "AgentPolicyStep" });

export const AgentPolicyExplain = z
  .object({
    seat_id: Uuid,
    class: z.string(),
    tool: z.string().nullable(),
    board_ids: z.array(Uuid),
    affected_count: z.int().nullable(),
    /** Every write path of the class holds the policy today (else the answer is advisory). */
    enforced: z.boolean(),
    /** The policy's answer (what enforcement decides), with its source and refusal. */
    verdict: AgentVerdict,
    source: AgentPolicySource,
    refusal: AgentRefusal.nullable(),
    /** What the agent meets on today's write paths, and why when it differs. */
    in_practice: AgentVerdict,
    note: z.string().nullable(),
    /** Every check of the rule in order. */
    steps: z.array(AgentPolicyStep),
    /**
     * The key scopes a run started from one of the seat's keys needs first (a runtime-minted run
     * is not held by a key): an empty `needs` means work scopes do not hold this class.
     */
    work: z.object({
      needs: z.array(z.object({ family: z.string(), access: z.enum(["read", "write"]) })),
      /** The class covers operations of different needs: ask by tool for one answer. */
      varies: z.boolean(),
      /** Null when `varies`. */
      keys: z.array(
        z.object({ id: Uuid, key_prefix: z.string(), allowed: z.boolean().nullable() }),
      ),
    }),
  })
  .meta({ id: "AgentPolicyExplain" });
export type AgentPolicyExplain = z.infer<typeof AgentPolicyExplain>;

export const agentPolicyRoutes = {
  "seats.agentPolicyPreview": defineRoute({
    feature: "agents",
    tag: "agent-policy",
    id: "seats.agentPolicyPreview",
    method: "get",
    path: `${co}/seats/{id}/agent-policy/preview`,
    summary: "Every action class's effective verdict for a seat's agent, and its source",
    description:
      "Managers and the holder of the seat's supervisor seat. `boardIds` evaluates the boards an operation touches: board rows only tighten.",
    access: "member",
    params: CompanyItemParams,
    query: AgentPolicyPreviewQuery,
    response: { status: 200, schema: AgentPolicyPreview },
    errors: [],
    emits: [],
  }),
  "seats.agentPolicy": defineRoute({
    feature: "agents",
    tag: "agent-policy",
    id: "seats.agentPolicy",
    method: "get",
    path: `${co}/seats/{id}/agent-policy`,
    summary: "A seat's agent policy: per class its mode, source and default; the keys' scopes",
    description:
      "Managers and the holder of the seat's supervisor seat. `version` (and the `ETag`) is the `If-Match` of a PUT.",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: SeatAgentPolicy },
    errors: [],
    emits: [],
  }),
  "seats.agentPolicyUpdate": defineRoute({
    feature: "agents",
    tag: "agent-policy",
    id: "seats.agentPolicyUpdate",
    method: "put",
    path: `${co}/seats/{id}/agent-policy`,
    summary: "Set a seat's agent policy modes per class (If-Match)",
    description:
      "Managers (owner, admin, agency operator). Unknown classes and hard prohibitions are 422; nothing is written. `inherit` removes the seat's row.",
    access: "manager",
    params: CompanyItemParams,
    body: SeatAgentPolicyPut,
    ifMatch: true,
    response: { status: 200, schema: SeatAgentPolicy },
    errors: ["seat_closed"],
    emits: ["agent_policy.changed"],
  }),
  "seats.agentPolicyExplain": defineRoute({
    feature: "agents",
    tag: "agent-policy",
    id: "seats.agentPolicyExplain",
    method: "get",
    path: `${co}/seats/{id}/agent-policy/explain`,
    summary: "Can this seat's agent do X on Y? The verdict and its reason chain",
    description:
      "Managers and the holder of the seat's supervisor seat. Read-only: the decision is the one enforcement takes.",
    access: "member",
    params: CompanyItemParams,
    query: AgentPolicyExplainQuery,
    response: { status: 200, schema: AgentPolicyExplain },
    errors: [],
    emits: [],
  }),
} as const;
